/* eslint-disable react/no-multi-comp */
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import * as R from 'ramda'
import {
  DateUtils,
  type Nil,
  PermissionArea,
  StateLabel,
  Utils,
} from '@pbt/pbt-ui-components'
import {
  Decline as DeclineIcon,
  Edit as EditIcon,
  Estimates as EstimatesIcon,
  Prescription as PrescriptionIcon,
  Print as PrintIcon,
} from '@pbt/pbt-ui-components/src/icons'

import type { PopperAction } from '~/components/common/ActionsPopper'
import LandingWidget, {
  type LandingWidgetProps,
} from '~/components/common/LandingWidget'
import NoRecords from '~/components/common/NoRecords'
import TableWidget from '~/components/common/TableWidget'
import FeatureToggle from '~/constants/featureToggle'
import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import { OrderType } from '~/constants/SOAPStates'
import usePrescriptions from '~/hooks/usePrescriptions'
import i18n from '~/locales/i18n'
import {
  fetchChargesByLog,
  getCharges,
  getChargesLoading,
} from '~/store/duck/charges'
import { getWidgetData, getWidgetDataIsLoading } from '~/store/duck/landing'
import { getCRUDByArea, getCurrentBusinessId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { PrescriptionSnapshotItem, WidgetColumn } from '~/types'
import { isChargesInvoiceType } from '~/utils/finance'
import { getPrescriptionType } from '~/utils/prescription'
import {
  getIdFromTimelineItem,
  getPrescriptionCanBeRefilled,
  getRemainingRefillsCount,
} from '~/utils/prescriptionUtils'

import PrescriptionNameCell from './PrescriptionNameCell'
import PrescriptionSnapShotLoadingOrIcon from './PrescriptionSnapShotLoadingOrIcon'

const RefillsLabel = ({
  numberRefills,
  isCancelled = false,
}: {
  isCancelled?: boolean
  numberRefills: number | Nil
}) => {
  if (!numberRefills) {
    return null
  }

  const numberRefillsLabel = isCancelled
    ? i18n.t('Common:CANCELLED')
    : i18n.t('Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS', {
        number: numberRefills,
      })

  return <StateLabel normal>{numberRefillsLabel}</StateLabel>
}

const columns: WidgetColumn<PrescriptionSnapshotItem>[] = [
  {
    id: 'name',
    component: PrescriptionNameCell,
    width: 4.25,
    label: i18n.t('Common:NAME'),
  },
  {
    id: 'doctorName',
    prop: ({ prescription }) =>
      prescription?.doctor
        ? Utils.getPersonString(prescription?.doctor)
        : undefined,
    width: 2,
    label: i18n.t('Common:DOCTOR'),
  },
  {
    id: 'date',
    prop: ({ date }) => DateUtils.formatDate(date),
    label: i18n.t('Common:PRESCRIBED'),
    width: 2.25,
  },
  {
    id: 'refills',
    prop: ({ prescription }) => (
      <RefillsLabel
        isCancelled={prescription && Boolean(prescription.cancelledBy)}
        numberRefills={prescription && getRemainingRefillsCount(prescription)}
      />
    ),
    width: 2,
    label: i18n.t('Common:REFILL_OTHER'),
  },
]

interface PrescriptionsSnapshotProps extends Omit<
  LandingWidgetProps<PrescriptionSnapshotItem>,
  'component' | 'data'
> {
  clientId: string
  name: string
  patientId: string
}

const PrescriptionsSnapshot = ({
  name,
  clientId,
  patientId,
  ...rest
}: PrescriptionsSnapshotProps) => {
  const { t } = useTranslation(['Abbreviations', 'Common', 'Clients'])

  const dispatch = useDispatch()

  const {
    handleGoToSoap,
    handleGoToInvoice,
    handleNavigateToItemEditableDetails,
    handlePrint,
    handleDispenseRefill,
    handleCancelRefills,
    handleAddPrescription,
  } = usePrescriptions({ clientId, patientId })

  const currentBusinessId = useSelector(getCurrentBusinessId)
  const charges = useSelector(getCharges)
  const chargesLoading = useSelector(getChargesLoading)
  const {
    create: invoiceCreatePermissions,
    read: invoiceReadPermissions,
    update: invoiceUpdatePermissions,
  } = useSelector(getCRUDByArea(PermissionArea.INVOICE))
  const { read: soapReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.SOAP),
  )
  const { update: prescriptionUpdatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.PRESCRIPTION),
  )

  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const prescriptionsAndOrders: PrescriptionSnapshotItem[] = useSelector(
    getWidgetData(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Prescriptions,
    ),
  )

  const isImportedPrescription = (item?: PrescriptionSnapshotItem) =>
    item?.type === 'HISTORY'

  const prescriptions = prescriptionsAndOrders.filter(
    (item) =>
      item.type === OrderType.PRESCRIPTION || isImportedPrescription(item),
  )

  const isLoading = useSelector(
    getWidgetDataIsLoading(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Prescriptions,
    ),
  )

  const getActions = (
    item?: PrescriptionSnapshotItem,
    refetchEntity?: boolean,
  ) => {
    if (!item) {
      return []
    }

    if (refetchEntity) {
      dispatch(
        fetchChargesByLog({
          logId: R.path(['prescription', 'id'], item),
          logType: OrderType.PRESCRIPTION,
        }),
      )
    }

    const isCurrentContextItem =
      !isPatientSharingEnabled || item.businessId === currentBusinessId
    const isInvoice = isChargesInvoiceType(charges)

    return isImportedPrescription(item)
      ? []
      : ([
          isCurrentContextItem &&
            item.prescription && {
              id: 'edit',
              label: t('Common:EDIT_ACTION'),
              Icon: PrescriptionSnapShotLoadingOrIcon,
              IconProps: { Icon: EditIcon },
              onClick:
                (invoiceUpdatePermissions && !item.soapId) ||
                (item.soapId && prescriptionUpdatePermissions)
                  ? () =>
                      handleNavigateToItemEditableDetails({
                        soapId: item.soapId,
                        prescription: item.prescription,
                        businessId: item.businessId || '',
                      })
                  : undefined,
              disabled: item.soapId
                ? !prescriptionUpdatePermissions
                : !invoiceUpdatePermissions,
            },
          isCurrentContextItem &&
            item.prescription &&
            getPrescriptionType(item.prescription.prescriptionType)
              ?.isInHouse && {
              id: 'print',
              label: t('Common:PRINT_ACTION'),
              Icon: PrintIcon,
              onClick: invoiceReadPermissions
                ? () => handlePrint(item)
                : undefined,
              disabled: !invoiceReadPermissions,
            },
          isCurrentContextItem &&
            Boolean(getIdFromTimelineItem(item)) && {
              id: 'goToInvoice',
              label: chargesLoading
                ? ''
                : isInvoice
                  ? t('Common:GO_TO_INVOICE')
                  : t('Common:GO_TO_CHARGE_SHEET'),
              Icon: PrescriptionSnapShotLoadingOrIcon,
              IconProps: { Icon: EstimatesIcon },
              onClick: invoiceReadPermissions
                ? () => handleGoToInvoice(item.prescription)
                : undefined,
              disabled: !invoiceReadPermissions,
            },
          item.soapId &&
            !item.isOTC && {
              id: 'goToSoap',
              label: t('Common:GO_TO_SOAP'),
              Icon: ArrowForwardIcon,
              onClick: soapReadPermissions
                ? () => handleGoToSoap(item.soapId, item.businessId)
                : undefined,
              disabled: !soapReadPermissions,
            },
          isCurrentContextItem &&
            item.prescription &&
            getPrescriptionCanBeRefilled(item.prescription) && {
              id: 'dispenseRefill',
              label: t('Common:DISPENSE_REFILL'),
              Icon: PrescriptionIcon,
              onClick: invoiceUpdatePermissions
                ? () => handleDispenseRefill(item)
                : undefined,
              disabled: !invoiceUpdatePermissions,
            },
          isCurrentContextItem &&
            item.prescription &&
            getPrescriptionCanBeRefilled(item.prescription) && {
              id: 'cancelRefills',
              label: t('Common:CANCEL_ALL_REFILLS'),
              Icon: DeclineIcon,
              onClick: invoiceUpdatePermissions
                ? () => handleCancelRefills(item.prescription)
                : undefined,
              disabled: !invoiceUpdatePermissions,
            },
        ].filter(Boolean) as PopperAction[])
  }

  const canNavigateToItemEditableDetails = (item: PrescriptionSnapshotItem) => {
    const hasRequiredRelations = Boolean(item?.soapId || item?.invoiceId)
    const isCurrentContextItem =
      !isPatientSharingEnabled || item?.businessId === currentBusinessId
    return isCurrentContextItem && hasRequiredRelations
  }

  return (
    <LandingWidget
      showHeaderTitles
      canNavigateToDetails={false}
      canNavigateToItemDetails={canNavigateToItemEditableDetails}
      columns={columns}
      component={TableWidget}
      data={prescriptions}
      getActions={getActions}
      isLoading={isLoading}
      name={name}
      noRecords={
        <NoRecords
          action={t('Abbreviations:ACRONYMS.OVER_THE_COUNTER.PRESCRIBE_OTC')}
          text={t('Clients:SNAPSHOTS_AND_RECORDS.NO_PRESCRIPTIONS')}
          onClick={invoiceCreatePermissions ? handleAddPrescription : undefined}
        />
      }
      showPaddings={false}
      onNavigateToItemDetails={(item) =>
        item.prescription && item.businessId
          ? handleNavigateToItemEditableDetails({
              soapId: item.soapId || '',
              prescription: item.prescription,
              businessId: item.businessId,
            })
          : undefined
      }
      {...rest}
    />
  )
}

export default PrescriptionsSnapshot
